/**
 * Strips HTML tags and decodes common entities to plain text.
 * @param {string} html - String that may contain HTML
 * @returns {string} Plain text without tags
 */
export const stripHtml = html => {
  if (html == null || typeof html !== 'string') {
    return '';
  }
  const withoutTags = html.replace(/<[^>]*>/g, ' ');
  return withoutTags
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#34;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

/** Unicode chars that can show as visible "return" / line-break symbols from rich text editors */
const RICH_TEXT_STRIP_CHARS =
  /[\u21b5\u21a9\u23ce\u00b6\u240d\u2424\u2936\u200b\u200c\u200d\ufeff]/g;
/** HTML entities for the same symbols (e.g. &#8629; for ↩) */
const RICH_TEXT_STRIP_ENTITIES =
  /&#8629;|&#x21a9;|&#8617;|&#x21b5;|&#9182;|&#x23ce;|&#182;|&#x00b6;|&#9229;|&#x240d;|&#9252;|&#x2424;|&#10550;|&#x2936;/gi;

/**
 * Sanitizes HTML for safe display, keeping formatting (paragraphs, lists, line breaks).
 * Removes script, style, event handlers, inline styles (colors/fonts), and visible line-break characters.
 * @param {string} html - String that may contain HTML
 * @returns {string} Sanitized HTML safe for dangerouslySetInnerHTML
 */
export const sanitizeHtml = html => {
  if (html == null || typeof html !== 'string') {
    return '';
  }
  let out = html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    .replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/\s*on\w+\s*=\s*[^\s>]*/gi, '')
    .replace(/<-\s*(\r?\n)?/g, '$1')
    .replace(/<-\s*$/gm, '')
    .replace(RICH_TEXT_STRIP_ENTITIES, '')
    .replace(RICH_TEXT_STRIP_CHARS, '');
  // Strip inline style attributes so app theme colors are used, not editor colors
  out = out.replace(/\s*style\s*=\s*["'][^"']*["']/gi, '');
  return out;
};

export const extractVideo = url => {
  if (typeof url !== 'string') {
    return '';
  }

  const trimmedUrl = url.trim();
  if (!trimmedUrl) {
    return '';
  }

  const normalizedUrl = trimmedUrl
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#34;/g, '"')
    .replace(/&#39;/g, "'");

  const iframeMatch = normalizedUrl.match(/src=["']([^"']+)["']/i);
  if (iframeMatch && iframeMatch[1]) {
    return extractVideo(iframeMatch[1]);
  }

  const match = normalizedUrl.match(
    /(?:youtube\.com\/(?:watch\?.*v=|embed\/|shorts\/|live\/)|youtu\.be\/)([^&?/]+)/
  );

  if (match && match[1]) {
    return match[1];
  }

  const queryParamVideoId = getQueryParam(normalizedUrl, 'v');
  if (queryParamVideoId) {
    return queryParamVideoId.split('&')[0];
  }

  if (/^[a-zA-Z0-9_-]{11}$/.test(normalizedUrl)) {
    return normalizedUrl;
  }

  return '';
};

export const extractVimeoId = url => {
  if (typeof url !== 'string') {
    return '';
  }

  const trimmedUrl = url.trim();
  if (!trimmedUrl) {
    return '';
  }

  const normalizedUrl = trimmedUrl
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#34;/g, '"')
    .replace(/&#39;/g, "'");

  const iframeMatch = normalizedUrl.match(/src=["']([^"']+)["']/i);
  if (iframeMatch && iframeMatch[1]) {
    return extractVimeoId(iframeMatch[1]);
  }

  const match = normalizedUrl.match(/(?:vimeo\.com\/(?:video\/)?)(\d+)/);
  if (match && match[1]) {
    return match[1];
  }

  const queryParamVideoId = getQueryParam(normalizedUrl, 'vimeo_id');
  if (queryParamVideoId) {
    return queryParamVideoId.split('&')[0];
  }

  if (/^\d+$/.test(normalizedUrl)) {
    return normalizedUrl;
  }

  return '';
};

export const getQueryParam = (url, param) => {
  const queryString = url.split('?')[1];
  if (!queryString) return null;

  const params = queryString.split('&');
  for (let i = 0; i < params.length; i += 1) {
    const [key, value] = params[i].split('=');
    if (key === param) {
      return decodeURIComponent(value);
    }
  }

  return null;
};
